import { useMemo, useState } from "react";
import { CalendarClock } from "lucide-react";
import {
  RSVP,
  rsvpLabel,
  effectiveRsvp,
  buildRsvpSummary,
} from "../../../lib/rsvp-utils";
import {
  updateSchedulerRsvp,
  upsertSchedulerVote,
} from "../../../lib/data/schedulers";
import { serverTimestamp } from "firebase/firestore";
import { VotingAvatarStack } from "../../../components/ui/voter-avatars";
import { buildColorMap } from "../../../components/ui/voter-avatar-utils";
import {
  anonymizeUsers,
  buildAnonymousIdentityMap,
} from "../../../lib/anonymous-voter-identities";

export function RsvpPanel({
  schedulerId,
  user,
  userVote,
  allVotes,
  winningSlotId,
  participantProfiles,
  identityDisplayMode = "named",
  // Phase 3a-cascade T4.5 — fires after a successful RSVP write that
  // transitions the user out of attending state on a finalized poll.
  // Parent uses this to open the PropagateVotesPromptDialog asking
  // whether to restore prior votes on other open polls.
  onAttendingTransitionedOff,
}) {
  const [saving, setSaving] = useState(false);

  const currentRsvp = useMemo(
    () => effectiveRsvp(userVote, winningSlotId),
    [userVote, winningSlotId]
  );

  const participantIds = useMemo(
    () => (participantProfiles || []).map((p) => p.uid || p.id),
    [participantProfiles]
  );

  const summary = useMemo(
    () => buildRsvpSummary(allVotes, winningSlotId, participantIds),
    [allVotes, winningSlotId, participantIds]
  );

  const shouldShowIdentities = identityDisplayMode !== "hidden";
  const isAnonymous = identityDisplayMode === "anonymous";

  const participantEmailMap = useMemo(() => {
    const map = {};
    (participantProfiles || []).forEach((p) => {
      const id = p.uid || p.id;
      if (id) map[id] = p;
    });
    return map;
  }, [participantProfiles]);

  const enrichIds = (ids) =>
    ids.map((id) => participantEmailMap[id] || { uid: id, email: id });

  const colorMap = useMemo(
    () =>
      buildColorMap(
        (participantProfiles || []).map((p) => p.email).filter(Boolean)
      ),
    [participantProfiles]
  );

  const anonymousIdentityMap = useMemo(() => {
    if (!isAnonymous) return null;
    const allUsers = (participantProfiles || []).map((p) => ({
      ...p,
      uid: p.uid || p.id,
    }));
    return buildAnonymousIdentityMap(allUsers, {
      scopeKey: `rsvp:scheduler:${schedulerId}`,
    });
  }, [isAnonymous, participantProfiles, schedulerId]);

  const maybeAnonymize = (users) => {
    if (!isAnonymous) return users;
    return anonymizeUsers(users, anonymousIdentityMap, { currentUser: user });
  };

  const handleRsvp = async (value) => {
    if (saving || !user?.uid) return;
    const newValue = value === currentRsvp ? null : value;
    const prevRsvp = currentRsvp;
    // "Attending" intent corresponds to GOING. Treat MAYBE as ambiguous —
    // user might still come, so we don't retract their busyWindow yet.
    // CANT_GO and null (untoggled, no response) both clearly leave the
    // attending set.
    const wasAttending = prevRsvp === RSVP.GOING;
    const isAttending = newValue === RSVP.GOING;
    const transitionedOff =
      wasAttending && !isAttending && Boolean(winningSlotId);

    setSaving(true);
    try {
      await updateSchedulerRsvp(schedulerId, user.uid, newValue);

      // Phase 3a-cascade T4.5 — when the user leaves the attending set on
      // a finalized poll, also clear their slot vote on the winning slot.
      // The busy-windows.js trigger reads `votes[winningSlotId]` (not the
      // rsvp field) when deciding whether to retract a busyWindow, so
      // this write is what actually causes other polls' auto-rsvp-cascade
      // overrides to clear. Add the slot to userTouchedSlots so the
      // engine doesn't re-cast it on the next re-eval.
      if (transitionedOff) {
        const existingTouched = Array.isArray(userVote?.userTouchedSlots)
          ? userVote.userTouchedSlots
          : [];
        const nextTouched = existingTouched.includes(winningSlotId)
          ? existingTouched
          : [...existingTouched, winningSlotId];
        await upsertSchedulerVote(schedulerId, user.uid, {
          votes: { [winningSlotId]: null },
          voteAutoSource: { [winningSlotId]: null },
          voteAutoReason: { [winningSlotId]: null },
          userTouchedSlots: nextTouched,
          updatedAt: serverTimestamp(),
        });

        if (typeof onAttendingTransitionedOff === "function") {
          try {
            await onAttendingTransitionedOff({
              prevRsvp,
              nextRsvp: newValue,
              schedulerId,
              winningSlotId,
            });
          } catch (cbErr) {
            // Non-blocking — the RSVP write already succeeded.
            console.error("onAttendingTransitionedOff failed:", cbErr);
          }
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const buttons = [
    { value: RSVP.GOING, activeClass: "bg-emerald-600 text-white border-emerald-600 dark:bg-emerald-500 dark:border-emerald-500", inactiveClass: "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/30" },
    { value: RSVP.MAYBE, activeClass: "bg-amber-500 text-white border-amber-500 dark:bg-amber-500 dark:border-amber-500", inactiveClass: "border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30" },
    { value: RSVP.CANT_GO, activeClass: "bg-rose-600 text-white border-rose-600 dark:bg-rose-500 dark:border-rose-500", inactiveClass: "border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/30" },
  ];

  const summaryRows = [
    { key: "going", label: "Going", ids: summary.going, colorClass: "text-emerald-600 dark:text-emerald-300" },
    { key: "maybe", label: "Maybe", ids: summary.maybe, colorClass: "text-amber-600 dark:text-amber-300" },
    { key: "cantGo", label: "Can't go", ids: summary.cantGo, colorClass: "text-rose-600 dark:text-rose-300" },
    { key: "noResponse", label: "No response", ids: summary.noResponse, colorClass: "text-slate-500 dark:text-slate-400" },
  ];

  return (
    <div
      className="mt-6 rounded-2xl border border-slate-200/70 bg-gradient-to-b from-slate-50 to-white p-5 dark:border-slate-700 dark:from-slate-900 dark:to-slate-800/80"
      data-testid="rsvp-panel"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
          RSVP
        </p>
      </div>

      {userVote?.rsvpSource === "auto-conflict" &&
      userVote?.autoRsvpState?.active &&
      userVote?.autoRsvpState?.winningSlotId === winningSlotId ? (
        <div
          data-testid="rsvp-auto-badge"
          className="mt-3 flex items-start gap-2 rounded-lg border border-status-conflict/50 bg-status-conflict/10 px-3 py-2 text-xs text-status-conflict"
        >
          <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Set automatically because of a calendar conflict. Pick a button below
            to override — we won&apos;t change it again.
          </span>
        </div>
      ) : null}

      <div className="mt-3 flex gap-2">
        {buttons.map(({ value, activeClass, inactiveClass }) => {
          const isActive = currentRsvp === value;
          return (
            <button
              key={value}
              type="button"
              disabled={saving}
              onClick={() => handleRsvp(value)}
              data-testid={`rsvp-btn-${value}`}
              data-active={isActive ? "true" : "false"}
              aria-pressed={isActive}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-all disabled:opacity-50 ${
                isActive ? `${activeClass} shadow-sm` : inactiveClass
              }`}
            >
              {rsvpLabel(value)}
            </button>
          );
        })}
      </div>

      <div
        className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4"
        data-testid="rsvp-summary"
      >
        {summaryRows.map(({ key, label, ids, colorClass }) => (
          <div
            key={key}
            data-testid={`rsvp-summary-${key}`}
            className="flex flex-col gap-1"
          >
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-semibold ${colorClass}`}>{label}</span>
              <span className="text-xs text-slate-400 dark:text-slate-500">{ids.length}</span>
            </div>
            {shouldShowIdentities && ids.length > 0 ? (
              <VotingAvatarStack
                users={maybeAnonymize(enrichIds(ids))}
                size={18}
                colorMap={colorMap}
              />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
